import { useState } from 'react';

import { countersApi, type CounterTable } from '@/api/queries/counters';
import { Button, Icon, Modal, useToast } from '@/components/ui';

interface Props {
  table: CounterTable;
  onClose: () => void;
}

export function ImportCounterCsvModal({ table, onClose }: Props) {
  const importCsv = countersApi.useImportCSV();
  const toast = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [dryRun, setDryRun] = useState(false);
  const [result, setResult] = useState<{ imported: number; dryRun?: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The header set ImportCsvAsync understands — mirrors the export, so an exported
  // file round-trips as a template.
  const expectedColumns = [
    'key',
    'parent',
    ...table.langs.flatMap((l) => [`value:${l}`, `description:${l}`]),
    ...(table.customFields ?? []).map((f) => `custom:${f.key}`),
  ];

  const submit = async () => {
    if (!file) {
      toast.show({ severity: 'error', title: 'Choose a CSV file' });
      return;
    }
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.show({ severity: 'error', title: 'File must be a .csv' });
      return;
    }
    setError(null);
    try {
      const res = await importCsv.mutateAsync({ key: table.key, file, dryRun });
      setResult(res);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Import failed';
      setError(msg);
      toast.show({ severity: 'error', title: msg });
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Import CSV"
      subtitle={`Add or update rows in ${table.name}. Rows are matched by key.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button variant="primary" loading={importCsv.isPending} onClick={submit} disabled={!file}>
            {dryRun ? 'Validate' : 'Import'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 14 }}>
        <div
          style={{
            padding: 12,
            border: '1px solid var(--border)',
            borderRadius: 8,
            background: 'var(--surface-2)',
            fontSize: 13,
            color: 'var(--text-2)',
          }}
        >
          <strong>Expected columns</strong>
          <div style={{ marginTop: 6 }}>
            <code style={{ fontSize: 12 }}>{expectedColumns.join(', ')}</code>
          </div>
          <div style={{ marginTop: 6, color: 'var(--text-3)' }}>
            Only <code>key</code> and <code>value:{table.defaultLang}</code> are required. Tip:
            Export CSV produces a compatible template.
          </div>
        </div>

        <input
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => {
            setFile(e.currentTarget.files?.[0] ?? null);
            setResult(null);
            setError(null);
          }}
        />

        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <input
            type="checkbox"
            checked={dryRun}
            onChange={(e) => setDryRun(e.currentTarget.checked)}
          />
          Dry run — validate the file without changing any rows
        </label>

        {result && (
          <div
            style={{
              padding: 12,
              border: '1px solid var(--green-soft, #bbf7d0)',
              background: 'var(--green-bg, #f0fdf4)',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Icon name="check" size={14} />
            {result.dryRun
              ? `Dry run: ${result.imported} row${result.imported === 1 ? '' : 's'} would be imported.`
              : `Imported ${result.imported} row${result.imported === 1 ? '' : 's'}.`}
          </div>
        )}

        {error && (
          <div
            style={{
              padding: 12,
              border: '1px solid var(--red-soft, #fecaca)',
              background: 'var(--red-bg, #fef2f2)',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Icon name="alert" size={14} />
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}
